import { unassignMachineFromCustomer } from '@/redux/actions/gpuActions';
import Badge from '@/shared/components/Badge';
import { Card, CardBody } from '@/shared/components/Card';
import ConfirmationModal from '@/shared/components/ConformationModal';
import ReactDataTableComponent from '@/shared/components/ReactDataTableComponent';
import ToolTipButton from '@/shared/components/ToolTipButton';
import { colorTextAdditional } from '@/utils/palette';
import { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import HelpModal from './GpuOneHelpModal';

const GpuOneTable = ({
	data = [],
	isLoading = false,
	columns = [],
	onRowClick,
	showActions = true,
	actionButton,
	className = '',
	isStnUser = false,
	onDataRefresh,
	// Server-side sorting props (similar to other tables)
	sortServer = false,
	onSort,
	defaultSortFieldId,
	defaultSortAsc = false,
	// Pagination props
	pagination = false,
	paginationServer = false,
	paginationTotalRows,
	onChangePage,
	paginationPerPage = 10,
	onChangeRowsPerPage,
	paginationRowsPerPageOptions = [10, 20, 30, 50],
	paginationDefaultPage = 1,
}) => {
	const dispatch = useDispatch();
	const [showUnassignConfirmation, setShowUnassignConfirmation] =
		useState(false);
	const [selectedRow, setSelectedRow] = useState(null);
	const [showHelpModal, setShowHelpModal] = useState(false);

	// Use dummy data if no data is provided
	// const tableData = data.length > 0 ? data : [];

	// Parse network interfaces to extract IP addresses
	const extractIPs = networkInterfaces => {
		if (!networkInterfaces || !Array.isArray(networkInterfaces))
			return 'No IPs found';

		const ips = [];
		networkInterfaces.forEach(iface => {
			const ipMatch = iface.match(/\[IPs: ([^\]]+)\]/);
			if (ipMatch) {
				ips.push(ipMatch[1]);
			}
		});

		return ips.length > 0 ? ips.join(', ') : 'No IPs found';
	};

	// Default columns if none provided
	const defaultColumns = useMemo(
		() => [
			{
				id: 'machine_name',
				name: 'Machine Name',
				selector: row => row.machine_name,
				sortable: true,
				cell: row => (
					<MachineNameCell onClick={() => onRowClick?.(row)}>
						{row.machine_name}
					</MachineNameCell>
				),
			},
			{
				id: 'system_vendor',
				name: 'System Vendor',
				selector: row => row.system_vendor,
				sortable: true,
			},
			{
				id: 'system_product',
				name: 'System Product',
				selector: row => row.system_product,
				sortable: true,
			},
			{
				id: 'mainboard_product',
				name: 'Mainboard Product',
				selector: row => row.mainboard_product,
				sortable: true,
				cell: row => (
					<SystemProductCell>
						<div>{row.mainboard_product}</div>
						<VersionText>v{row.mainboard_version}</VersionText>
					</SystemProductCell>
				),
			},
			{
				id: 'total_cpu_cores',
				name: 'Total CPU Cores',
				selector: row => row.total_cpu_cores,
				sortable: true,
				cell: row => (
					<CoresCell>
						<CoresNumber>{row.total_cpu_cores}</CoresNumber>
						<CoresLabel>cores</CoresLabel>
					</CoresCell>
				),
			},
			{
				id: 'cpu_type',
				name: 'CPU Type',
				selector: row => row.cpu_type,
				sortable: true,
				cell: row => (
					<CPUCell>
						<CPUType>
							{row.cpu_type?.split(' ')[0]} {row.cpu_type?.split(' ')[1]}
						</CPUType>
						<CPUDetails>
							{row.cpu_type?.split(' ').slice(2).join(' ')}
						</CPUDetails>
					</CPUCell>
				),
			},
			{
				id: 'total_memory',
				name: 'Total Memory (GB)',
				selector: row => row.total_memory,
				sortable: true,
				cell: row => (
					<MemoryCell>
						<MemoryNumber>{row.total_memory}</MemoryNumber>
						<MemoryUnit>GB</MemoryUnit>
					</MemoryCell>
				),
			},
			{
				id: 'network_interfaces',
				name: 'Network Interfaces',
				selector: row => extractIPs(row.network_interfaces),
				sortable: true,
				cell: row => {
					const primaryIP =
						extractIPs(row.network_interfaces).split(',')[0] || 'N/A';
					return (
						<NetworkCell>
							<PrimaryIP>{primaryIP}</PrimaryIP>
							<InterfaceCount>
								{Array.isArray(row.network_interfaces)
									? row.network_interfaces.length
									: 0}{' '}
								interfaces
							</InterfaceCount>
						</NetworkCell>
					);
				},
			},
			{
				id: 'machine_status',
				name: 'Machine Status',
				selector: row => row.machine_status,
				sortable: true,
				cell: row => (
					<StatusCell>
						<Badge bg={row.machine_status}>{row.machine_status}</Badge>
					</StatusCell>
				),
			},
			{
				id: 'deployed_os',
				name: 'Deployed OS',
				selector: row => row.deployed_os,
				sortable: true,
				cell: row => (
					<OSCell>
						<OSName>{row.deployed_os?.split(' ')[0]}</OSName>
						<OSVersion>
							{row.deployed_os?.split(' ').slice(1).join(' ')}
						</OSVersion>
					</OSCell>
				),
			},
		],
		[],
	);

	// Unassign functions
	const handleUnassignMachine = row => {
		setSelectedRow(row);
		setShowUnassignConfirmation(true);
	};

	const confirmUnassign = async () => {
		try {
			await dispatch(unassignMachineFromCustomer(selectedRow.system_serial));
			setShowUnassignConfirmation(false);
			setSelectedRow(null);
			// Refresh data if callback provided
			if (onDataRefresh) {
				onDataRefresh();
			}
		} catch (error) {
			console.error('Error unassigning machine:', error);
			setShowUnassignConfirmation(false);
			setSelectedRow(null);
		}
	};

	const cancelUnassign = () => {
		setShowUnassignConfirmation(false);
		setSelectedRow(null);
	};

	// Use provided columns or default columns
	const tableColumns = columns.length > 0 ? columns : defaultColumns;

	const handleHelp = row => {
		setShowHelpModal(true);
		setSelectedRow(row);
	};

	// Add actions column if showActions is true
	const finalColumns = showActions
		? [
				...tableColumns,
				{
					name: 'Actions',
					cell: row => (
						<ActionsCell>
							{actionButton ? (
								actionButton(row)
							) : (
								<>
									<ToolTipButton
										size="iconButton"
										tooltipText="Help"
										iconType="help"
										variant="primary"
										onClick={() => handleHelp(row)}
									>
										Help
									</ToolTipButton>
									{isStnUser && (
										<ToolTipButton
											size="iconButton"
											tooltipText="Unassign Machine"
											iconType="delete"
											variant="danger"
											onClick={() => handleUnassignMachine(row)}
											// style={{ marginLeft: '8px' }}
										>
											Unassign
										</ToolTipButton>
									)}
								</>
							)}
						</ActionsCell>
					),
					sortable: false,
					sticky: 'right',
					width: 'auto',
					minWidth: '120px',
				},
			]
		: tableColumns;

	return (
		<>
			<Row>
				<Col>
					<Card>
						<CardBody>
							{data.length > 0 ? (
								<ReactDataTableComponent
									columns={finalColumns}
									data={data}
									pagination={pagination}
									paginationServer={paginationServer}
									paginationTotalRows={paginationTotalRows}
									onChangePage={onChangePage}
									paginationPerPage={paginationPerPage}
									onChangeRowsPerPage={onChangeRowsPerPage}
									paginationRowsPerPageOptions={paginationRowsPerPageOptions}
									paginationDefaultPage={paginationDefaultPage}
									sortServer={sortServer}
									onSort={onSort}
									defaultSortFieldId={defaultSortFieldId}
									defaultSortAsc={defaultSortAsc}
									highlightOnHover={true}
									responsive={true}
									progressPending={isLoading}
									className={className}
									persistTableHead
								/>
							) : (
								<div>No data found</div>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>

			{showUnassignConfirmation && (
				<ConfirmationModal
					onCancel={cancelUnassign}
					onConfirm={confirmUnassign}
					title="Confirm Unassignment"
					message={`Are you sure you want to unassign the machine "${selectedRow?.machine_name}"? This action cannot be undone.`}
					confirmButtonText="Unassign Machine"
					variant="danger"
				/>
			)}

			{showHelpModal && (
				<HelpModal
					onClose={() => setShowHelpModal(false)}
					row={selectedRow}
					modal={showHelpModal}
					toggle={() => setShowHelpModal(false)}
					setModal={setShowHelpModal}
				/>
			)}
		</>
	);
};

// Styled Components
const MachineNameCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	cursor: pointer;
	text-decoration: underline;
`;

const SystemProductCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const VersionText = styled.span`
	font-size: 12px;
	color: ${colorTextAdditional};
	opacity: 0.7;
`;

const CoresCell = styled.div`
	display: flex;
	align-items: baseline;
	gap: 4px;
`;

const CoresNumber = styled.span`
	font-size: 1.1rem;
	font-weight: 600;
	color: #667eea;
`;

const CoresLabel = styled.span`
	font-size: 0.8rem;
	color: ${colorTextAdditional};
`;

const CPUCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	max-width: 200px;
`;

const CPUType = styled.span`
	font-weight: 500;
	font-size: 0.9rem;
`;

const CPUDetails = styled.span`
	font-size: 0.75rem;
	color: ${colorTextAdditional};
	line-height: 1.2;
`;

const MemoryCell = styled.div`
	display: flex;
	align-items: baseline;
	gap: 4px;
`;

const MemoryNumber = styled.span`
	font-size: 1.1rem;
	font-weight: 600;
	color: #764ba2;
`;

const MemoryUnit = styled.span`
	font-size: 0.8rem;
	color: ${colorTextAdditional};
`;

const NetworkCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	max-width: 150px;
`;

const PrimaryIP = styled.span`
	font-size: 0.85rem;
	color: #28a745;
`;

const InterfaceCount = styled.span`
	font-size: 0.75rem;
	color: ${colorTextAdditional};
`;

const StatusCell = styled.div`
	display: flex;
	justify-content: center;
`;

const OSCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const OSName = styled.span`
	font-weight: 500;
	text-transform: capitalize;
`;

const OSVersion = styled.span`
	font-size: 0.75rem;
	color: ${colorTextAdditional};
`;

const ActionsCell = styled.div`
	display: flex;
	justify-content: center;
	gap: 8px;
	min-width: fit-content;
`;

export default GpuOneTable;
